import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDeletePost } from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';
import useRefreshProjectCampaignStats from '../useRefreshProjectCampaignStats';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';

type OptimisticContext = {
    previousPosts: creatorbase.Post[] | undefined;
    campaignId: number;
    platform: creatorbase.Platform;
};

// Optimistic post delete scoped to a single ProjectDetails campaign list. The
// row is removed from the cache immediately so the table updates without
// waiting on the request, restored if it fails, and reconciled against the
// server plus the campaign/project stats cascade once settled.
//
// The campaign and platform are taken from the post being deleted, so the hook
// can live directly in the delete modal. The post is nullable while the modal
// is closed; the cache work only runs once a post is selected (a delete is only
// ever triggered with one present). The campaign/platform are snapshotted into
// the mutation context in onMutate rather than read from the live prop later,
// so the invalidation and stats refresh still run if the modal closes (and the
// prop goes null) before the request settles. As with the update hook the
// optimism is confined to this campaign's list key, and the mutation is
// presented as deletePost's response-returning contract so the modal branches
// on status unchanged.
export default function useDeleteProjectDetailsPost(post: creatorbase.Post | null) {
    const queryClient = useQueryClient();
    const refreshAllStats = useRefreshProjectCampaignStats();

    const { mutateAsync } = useDeletePost<OptimisticContext>({
        onMutate: async (postId) => {
            if (!post) {
                return;
            }

            const listKey = creatorbaseQueries.posts.keys.list(getProjectDetailsPostsParams(post.campaign_id));
            await queryClient.cancelQueries({ queryKey: listKey });
            const previousPosts = queryClient.getQueryData<creatorbase.Post[]>(listKey);

            queryClient.setQueryData<creatorbase.Post[]>(
                listKey,
                (posts) => posts?.filter((cachedPost) => cachedPost.id !== postId)
            );

            return { previousPosts, campaignId: post.campaign_id, platform: post.platform };
        },
        onError: (_error, _postId, context) => {
            if (context?.previousPosts) {
                const listKey = creatorbaseQueries.posts.keys.list(getProjectDetailsPostsParams(context.campaignId));
                queryClient.setQueryData(listKey, context.previousPosts);
            }
        },
        onSettled: (_data, _error, _postId, context) => {
            if (!context) {
                return;
            }

            queryClient.invalidateQueries({
                queryKey: creatorbaseQueries.posts.keys.list(getProjectDetailsPostsParams(context.campaignId)),
            });
            refreshAllStats(context.campaignId, context.platform).catch(() => {
                showNotification('Could not refresh data', 'error');
            });
        },
    });

    const deletePost: typeof creatorbase.deletePost = useCallback(
        async (postId) => {
            try {
                await mutateAsync(postId);
                return { status: 204, data: null };
            } catch (error) {
                if (error instanceof Error && creatorbaseQueries.posts.utils.delete.isExpectedErrorResponse(error)) {
                    return error.response;
                }

                throw error;
            }
        },
        [mutateAsync]
    );

    return { deletePost };
}
